import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Space,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Popconfirm,
  Tooltip,
  message,
} from "antd";
import { PlusOutlined, DeleteOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import { ThreeDot } from "react-loading-indicators";

import { getAllUsers, createUser, deleteUser, assignRoleToUser } from "../../api/users";
import { getRoles } from "../../api/roles";
import { useAuth } from "../../contexts/AuthProvider";
import { hasPermission } from "../../utils/auth";

// GET /users/all returns [{ id: null, role_name: null }] for a user with no
// roles (json_agg over a LEFT JOIN) instead of an empty array — filter those out.
const getAssignedRoles = (userRow) =>
  (userRow?.roles ?? []).filter((role) => role.id != null);

const isSuperAdminUser = (userRow) =>
  getAssignedRoles(userRow).some(
    (role) => role.role_name?.toLowerCase() === "superadmin",
  );

const UsersTab = () => {
  const { user: currentUser } = useAuth();
  const canWrite = hasPermission(currentUser?.permissions, "users:write");
  const canDelete = hasPermission(currentUser?.permissions, "users:delete");

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm] = Form.useForm();

  const [addRoleUser, setAddRoleUser] = useState(null);
  const [addRoleLoading, setAddRoleLoading] = useState(false);
  const [addRoleForm] = Form.useForm();

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const [usersData, rolesData] = await Promise.all([
          getAllUsers(),
          getRoles(),
        ]);
        setUsers(usersData);
        setRoles(rolesData);
      } catch (error) {
        console.log(error);
        message.error("No se pudieron cargar los usuarios.");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [refresh]);

  const handleCreateUser = async (values) => {
    setCreateLoading(true);
    try {
      const payload = {
        ...values,
        username: values.email,
        company_name: "Mundotito",
      };
      const created = await createUser(payload);
      await assignRoleToUser(created.email ?? payload.email, values.role);
      message.success("Usuario creado correctamente.");
      setCreateModalOpen(false);
      createForm.resetFields();
      setRefresh((prev) => !prev);
    } catch (error) {
      console.log(error);
      message.error("No se pudo crear el usuario.");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleAddRole = async (values) => {
    setAddRoleLoading(true);
    try {
      await assignRoleToUser(addRoleUser.email, values.role);
      message.success("Rol asignado correctamente.");
      setAddRoleUser(null);
      addRoleForm.resetFields();
      setRefresh((prev) => !prev);
    } catch (error) {
      console.log(error);
      message.error("No se pudo asignar el rol.");
    } finally {
      setAddRoleLoading(false);
    }
  };

  const handleDeleteUser = async (userRow) => {
    try {
      await deleteUser(userRow.user_id);
      message.success("Usuario eliminado correctamente.");
      setRefresh((prev) => !prev);
    } catch (error) {
      console.log(error);
      message.error("No se pudo eliminar el usuario.");
    }
  };

  const columns = [
    {
      title: "Nombre",
      key: "name",
      render: (_, row) => `${row.name} ${row.surname}`,
    },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Cargo", dataIndex: "position", key: "position" },
    {
      title: "Roles",
      key: "roles",
      render: (_, row) => {
        const assignedRoles = getAssignedRoles(row);
        return (
          <Space wrap>
            {assignedRoles.length === 0 ? (
              <span className="text-gray-400">Sin roles</span>
            ) : (
              assignedRoles.map((role) => (
                <Tag color="blue" key={role.id}>
                  {role.role_name}
                </Tag>
              ))
            )}
          </Space>
        );
      },
    },
    {
      title: "Estado",
      key: "is_active",
      render: (_, row) => (
        <Tag color={row.is_active ? "green" : "default"}>
          {row.is_active ? "Activo" : "Inactivo"}
        </Tag>
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_, row) => (
        <Space>
          {canWrite && (
            <Button
              size="small"
              icon={<UsergroupAddOutlined />}
              onClick={() => setAddRoleUser(row)}
            >
              Agregar rol
            </Button>
          )}
          {canDelete && (() => {
            const isSelf = row.email === currentUser?.email;
            const disableDelete = isSelf || isSuperAdminUser(row);
            const tooltip = isSelf
              ? "No puedes eliminar tu propia cuenta"
              : isSuperAdminUser(row)
                ? "No se puede eliminar a un usuario superadmin"
                : "";
            return (
              <Tooltip title={tooltip}>
                <span>
                  <Popconfirm
                    title="¿Eliminar este usuario?"
                    okText="Eliminar"
                    cancelText="Cancelar"
                    onConfirm={() => handleDeleteUser(row)}
                    disabled={disableDelete}
                  >
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={disableDelete}
                    >
                      Eliminar
                    </Button>
                  </Popconfirm>
                </span>
              </Tooltip>
            );
          })()}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-4 flex justify-end">
        {canWrite && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            Crear usuario
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="w-full text-center min-h-72 flex items-center justify-center">
          <ThreeDot variant="brick-stack" color="#4048F1" size="medium" />
        </div>
      ) : (
        <Table
          rowKey="user_id"
          columns={columns}
          dataSource={users}
          pagination={{ pageSize: 10 }}
        />
      )}

      {/* Create user modal */}
      <Modal
        title="Crear usuario"
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        onOk={() => createForm.submit()}
        confirmLoading={createLoading}
        okText="Crear"
        cancelText="Cancelar"
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateUser}
          initialValues={{ is_active: true }}
        >
          <div className="flex gap-3">
            <Form.Item
              name="name"
              label="Nombre"
              rules={[{ required: true }]}
              className="flex-1"
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="surname"
              label="Apellido"
              rules={[{ required: true }]}
              className="flex-1"
            >
              <Input />
            </Form.Item>
          </div>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, type: "email" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="password"
            label="Contraseña"
            rules={[{ required: true, min: 6 }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item name="position" label="Cargo" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="role" label="Rol inicial" rules={[{ required: true }]}>
            <Select
              options={roles.map((role) => ({
                value: role.id,
                label: role.role_name,
              }))}
              placeholder="Selecciona un rol"
            />
          </Form.Item>
          <Form.Item name="is_active" label="Activo" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Add role modal */}
      <Modal
        title={addRoleUser ? `Agregar rol a ${addRoleUser.name}` : "Agregar rol"}
        open={!!addRoleUser}
        onCancel={() => setAddRoleUser(null)}
        onOk={() => addRoleForm.submit()}
        confirmLoading={addRoleLoading}
        okText="Agregar"
        cancelText="Cancelar"
      >
        <Form form={addRoleForm} layout="vertical" onFinish={handleAddRole}>
          <Form.Item name="role" label="Rol" rules={[{ required: true }]}>
            <Select
              options={roles
                .filter(
                  (role) =>
                    !getAssignedRoles(addRoleUser).some(
                      (r) => r.id === role.id,
                    ),
                )
                .map((role) => ({ value: role.id, label: role.role_name }))}
              placeholder="Selecciona un rol"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UsersTab;
